import React from "react";
import { Typography, Link, Accordion, AccordionSummary, AccordionDetails, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import CopyrightOutlinedIcon from "@mui/icons-material/CopyrightOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import { useNavigate } from "react-router-dom";

const ACCENT = "#c79656";
const BORDER = "#EAECEF";
const TEXT = "#1a1a1a";
const MUTED = "rgba(26,26,26,0.55)";

const VERSION = "0.9.4";
const CREATED_BY = "Joey Koster";
const UPDATED_ON = "15 August 2026";
const EMAIL = "info@tipcounter.be";

interface Faq {
    q: string;
    a: React.ReactNode;
}

const FAQS: Faq[] = [
    {
        q: "How do I record tips for a work day?",
        a: "Open the Dashboard, pick a date on the calendar and enter the cash and pin tips for the day. Days with data are highlighted so you can see your history at a glance.",
    },
    {
        q: "What is the difference between manual and auto count?",
        a: "Manual Count lets you close out and reconcile a shift by hand, entering counted amounts step by step. Auto Count tallies everything for you from the recorded work periods — pick whichever fits your evening.",
    },
    {
        q: "How are tips split between workers?",
        a: "Assign the workers present for a work period and TipCounter distributes the pot based on your location's rules, rounding to whole amounts where needed.",
    },
    {
        q: "What is debt and how is it tracked?",
        a: "When you count a worker, TipCounter pays out an amount that fits your chosen coins and notes and carries the small leftover forward as debt — so nothing is lost between payouts. Marking a worker counted shows the debt moving from its old value to the new one, and unchecking them reverses exactly that change.",
    },
    {
        q: "Can I change the payout coins or rounding?",
        a: "Yes. Set the available denominations and whether payouts are exact or rounded on the Counting settings tab. You can also adjust them on the spot from the Payout options on the manual and auto count pages — the choice is shared everywhere.",
    },
    {
        q: "Two profiles exist for the same person — can I fix that?",
        a: "Yes. Open the Team page, choose a worker and use Merge to combine duplicate profiles into one without losing any history.",
    },
    {
        q: "Can I import existing data?",
        a: "You can import shifts and workers from a CSV file. Head to the relevant page and use the import option to bring your data across.",
    },
    {
        q: "Where can I see what recently changed?",
        a: "The What's New page lists every release and the features and fixes it introduced, newest first.",
    },
];

const QUICK_LINKS = [
    { label: "Dashboard", desc: "Daily tips & overview", icon: <SavingsOutlinedIcon />, to: "/dashboard" },
    { label: "Manual Count", desc: "Close out a shift", icon: <CalculateOutlinedIcon />, to: "/counting/manual" },
    { label: "Team", desc: "Manage your workers", icon: <GroupsOutlinedIcon />, to: "/organisation/team" },
    { label: "What's New", desc: "Latest release notes", icon: <NewReleasesOutlinedIcon />, to: "/whatsnew" },
];

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    background: `${ACCENT}14`,
                    color: ACCENT,
                }}
            >
                {icon}
            </div>
            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, fontWeight: 700 }}>
                    {label}
                </span>
                <span style={{ fontSize: 14, color: TEXT, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {value}
                </span>
            </div>
        </div>
    );
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    display: "grid",
                    placeItems: "center",
                    background: "#f6f7f9",
                    border: `1px solid ${BORDER}`,
                    color: ACCENT,
                }}
            >
                {icon}
            </div>
            <Typography variant="h6" style={{ fontWeight: 700, fontSize: 17 }}>
                {title}
            </Typography>
        </div>
    );
}

const cardStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: 16,
    border: `1px solid ${BORDER}`,
    boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
    padding: 22,
    boxSizing: "border-box",
};

export default function HelpPage() {
    const navigate = useNavigate();

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                padding: 24,
                display: "flex",
                flexDirection: "column",
                gap: 18,
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                <div
                    style={{
                        width: 46,
                        height: 46,
                        borderRadius: 14,
                        display: "grid",
                        placeItems: "center",
                        background: `${ACCENT}18`,
                        border: `1px solid ${ACCENT}33`,
                        color: ACCENT,
                    }}
                >
                    <HelpOutlineIcon />
                </div>
                <div>
                    <span style={{ fontSize: 26, fontWeight: 800, color: TEXT, letterSpacing: "-0.02em" }}>
                        Help &amp; Support
                    </span>
                    <div style={{ fontSize: 13.5, color: MUTED, fontWeight: 500 }}>
                        Answers, guidance and how to reach us
                    </div>
                </div>
            </div>

            {/* Scrollable content */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 8 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1.6fr) minmax(260px, 1fr)",
                        gap: 18,
                        alignItems: "start",
                    }}
                >
                    {/* Left column — FAQ + quick links */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
                        <div style={cardStyle}>
                            <SectionTitle icon={<HelpOutlineIcon />} title="Frequently asked questions" />
                            <div>
                                {FAQS.map((faq, i) => (
                                    <Accordion
                                        key={i}
                                        disableGutters
                                        elevation={0}
                                        square
                                        sx={{
                                            border: `1px solid ${BORDER}`,
                                            borderRadius: "12px !important",
                                            mb: 1,
                                            overflow: "hidden",
                                            "&:before": { display: "none" },
                                            "&:last-of-type": { mb: 0 },
                                            "&.Mui-expanded": { borderColor: `${ACCENT}66`, background: `${ACCENT}08` },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: ACCENT }} />}
                                            sx={{
                                                px: 2,
                                                minHeight: 52,
                                                "& .MuiAccordionSummary-content": { my: 1.25 },
                                            }}
                                        >
                                            <Typography style={{ fontWeight: 600, fontSize: 14.5, color: TEXT }}>
                                                {faq.q}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                                            <Typography variant="body2" style={{ color: "#4b5563", lineHeight: 1.6 }}>
                                                {faq.a}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </div>
                        </div>

                        <div style={cardStyle}>
                            <SectionTitle icon={<CalculateOutlinedIcon />} title="Jump to" />
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                    gap: 12,
                                }}
                            >
                                {QUICK_LINKS.map((link) => (
                                    <button
                                        key={link.label}
                                        onClick={() => navigate(link.to)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 12,
                                            textAlign: "left",
                                            padding: "12px 14px",
                                            borderRadius: 12,
                                            border: `1px solid ${BORDER}`,
                                            background: "#fff",
                                            cursor: "pointer",
                                            transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = `${ACCENT}66`;
                                            e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.06)";
                                            e.currentTarget.style.transform = "translateY(-1px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = BORDER;
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: 10,
                                                flexShrink: 0,
                                                display: "grid",
                                                placeItems: "center",
                                                background: `${ACCENT}14`,
                                                color: ACCENT,
                                            }}
                                        >
                                            {link.icon}
                                        </div>
                                        <div style={{ minWidth: 0, lineHeight: 1.3 }}>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>{link.label}</div>
                                            <div style={{ fontSize: 12, color: MUTED, fontWeight: 500 }}>{link.desc}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right column — app details + contact */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
                        <div style={cardStyle}>
                            <SectionTitle icon={<InfoOutlinedIcon />} title="Application details" />
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                <DetailRow icon={<CodeOutlinedIcon sx={{ fontSize: 18 }} />} label="Version" value={VERSION} />
                                <DetailRow icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />} label="Created by" value={CREATED_BY} />
                                <DetailRow icon={<UpdateOutlinedIcon sx={{ fontSize: 18 }} />} label="Last updated" value={UPDATED_ON} />
                            </div>
                            <div
                                style={{
                                    marginTop: 18,
                                    paddingTop: 16,
                                    borderTop: `1px solid ${BORDER}`,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    color: MUTED,
                                    fontSize: 12.5,
                                }}
                            >
                                <CopyrightOutlinedIcon sx={{ fontSize: 16 }} />
                                <span>{new Date().getFullYear()} TipCounter. All rights reserved.</span>
                            </div>
                        </div>

                        {/* Contact card */}
                        <div
                            style={{
                                ...cardStyle,
                                background: `linear-gradient(155deg, #fffdf9 0%, ${ACCENT}12 100%)`,
                                borderColor: `${ACCENT}40`,
                            }}
                        >
                            <div
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 12,
                                    display: "grid",
                                    placeItems: "center",
                                    background: ACCENT,
                                    color: "#fff",
                                    marginBottom: 12,
                                }}
                            >
                                <EmailOutlinedIcon />
                            </div>
                            <Typography variant="h6" style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                                Still need a hand?
                            </Typography>
                            <Typography variant="body2" style={{ color: "#4b5563", lineHeight: 1.6, marginBottom: 16 }}>
                                Can't find what you're looking for? Send us a message and we'll get back to you.
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                href={`mailto:${EMAIL}`}
                                startIcon={<EmailOutlinedIcon />}
                                disableElevation
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: 2.5,
                                    py: 1.1,
                                    background: ACCENT,
                                    "&:hover": { background: "#b3823f" },
                                }}
                            >
                                Contact support
                            </Button>
                            <div style={{ textAlign: "center", marginTop: 10 }}>
                                <Link href={`mailto:${EMAIL}`} underline="hover" style={{ fontSize: 12.5, color: MUTED }}>
                                    {EMAIL}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
